/** Startseite: Müll-Hinweis, Stempeluhr, heutige Einträge. */
import { elapsedMs, formatElapsed, isPaused, startClock, stopClock, togglePause } from '../clock.js';
import { currentMe, getClock, localMonth, saveChange, setClock } from '../store.js';
import { $, esc, options, toast } from '../ui.js';
import { loadVisits, visitsHtml } from './visits.js';
import { appliancesHtml, bindAppliances, loadAppliances } from './appliances.js';
import { formatRemaining } from '../../shared/appliances.js';
import { bindPutouts, loadWaste, wasteBanner } from './waste.js';
import { WEEKDAYS_LONG, berlinDate, berlinTime, formatDateDe, formatHM, monthOf, weekday, workMinutes } from '../../shared/time.js';
import { findOverlaps } from '../../shared/rules.js';

let tick: number | undefined;
let poll: number | undefined;
let countdown: number | undefined;

/** Gemeinsamer Status (Geräte, Müll) für alle: alle 25 s neu laden, solange die App sichtbar ist. */
function startPolling(): void {
  window.clearInterval(poll);
  window.clearInterval(countdown);
  poll = window.setInterval(() => {
    if (document.visibilityState !== 'visible') return;
    void refreshAppliancesBox();
    void refreshWasteBox();
    void refreshVisitsBox();
  }, 25_000);
  // Countdown sekündlich; springt ein Gerät auf „fertig", Kachel neu zeichnen.
  countdown = window.setInterval(() => {
    const now = Date.now();
    let due = false;
    document.querySelectorAll<HTMLElement>('[data-until]').forEach((el) => {
      const until = Number(el.dataset.until);
      if (now >= until) due = true;
      else el.textContent = formatRemaining(until - now);
    });
    if (due) void refreshAppliancesBox();
  }, 1000);
}

export async function refreshVisitsBox(): Promise<void> {
  const d = await loadVisits();
  const box = document.getElementById('visits');
  if (box) box.innerHTML = visitsHtml(d);
}

export async function refreshAppliancesBox(): Promise<void> {
  const d = await loadAppliances();
  const box = document.getElementById('appliances');
  if (!box) return;
  box.innerHTML = appliancesHtml(d);
  bindAppliances(box, () => void refreshAppliancesBox());
}

export async function renderHome(el: HTMLElement): Promise<void> {
  window.clearInterval(tick);
  const me = currentMe()!;
  const now = Date.now();
  const today = berlinDate(now);
  const clock = await getClock();
  const { entries } = await localMonth(monthOf(today));
  const todays = entries.filter((e) => e.date === today && e.end !== null);
  const sum = todays.reduce((s, e) => s + workMinutes(e.date, e.start, e.end!, e.pauseMin), 0);
  const lastActivity = (localStorage.getItem('ze:lastActivity') ?? me.activities[0]) || '';

  el.innerHTML = `
    <div id="waste"></div>
    <p class="today-line">${esc(WEEKDAYS_LONG[weekday(today)])}, ${esc(formatDateDe(today))}</p>
    <section class="clock ${clock ? (isPaused(clock) ? 'is-paused' : 'is-running') : 'is-idle'}" aria-live="polite">
      ${clock ? `
        <div class="clock-face"><span id="elapsed">${formatElapsed(elapsedMs(clock, now))}</span></div>
        <p class="clock-meta">${isPaused(clock) ? `Pause seit ${esc(berlinTime(clock.pauseStart!))}` : `Gekommen ${esc(clock.date === today ? '' : formatDateDe(clock.date) + ' ')}um ${esc(clock.start)}`}
          · ${esc(clock.activity)}${clock.pauseMin ? ` · ${clock.pauseMin} min Pause` : ''}</p>
        <div class="clock-actions">
          <button class="btn stamp pause" id="pause">${isPaused(clock) ? 'Weiter' : 'Pause'}</button>
          <button class="btn stamp go" id="stop">Gehen</button>
        </div>` : `
        <div class="clock-face idle"><span>${esc(berlinTime(now))}</span></div>
        <label class="clock-activity">Tätigkeit / Objekt
          <select id="activity">${options(me.activities, lastActivity)}</select></label>
        <div class="clock-actions"><button class="btn stamp come" id="start">Kommen</button></div>`}
    </section>

    <div id="visits"></div>

    <section class="today">
      <div class="section-head"><h2>Heute</h2><span class="sum">${formatHM(sum)} Std.</span></div>
      ${todays.length ? `<ul class="entry-list">${todays
        .map((e) => `<li><a href="#/eintrag/${esc(e.id)}">
          <span class="t">${esc(e.start)}–${esc(e.end)}</span>
          <span class="a">${esc(e.activity)}</span>
          <span class="d">${formatHM(workMinutes(e.date, e.start, e.end!, e.pauseMin))}</span>
          ${e.pending ? `<span class="badge ${e.error ? 'err' : 'wait'}">${e.error ? 'Fehler' : 'wartet'}</span>` : ''}</a></li>`)
        .join('')}</ul>` : '<p class="muted">Heute noch nichts erfasst.</p>'}
      <a class="btn" href="#/eintrag/neu">Zeit nachtragen</a>
    </section>
    <div id="appliances"></div>
  `;

  void refreshWasteBox();
  void refreshAppliancesBox();
  void refreshVisitsBox();
  startPolling();

  if (clock) {
    tick = window.setInterval(() => {
      const span = document.getElementById('elapsed');
      if (!span) return window.clearInterval(tick);
      span.textContent = formatElapsed(elapsedMs(clock, Date.now()));
    }, 1000);
    $('#pause', el).addEventListener('click', async () => {
      const next = togglePause(clock, Date.now());
      await setClock(next);
      await saveChange(next);
      void renderHome(el);
    });
    $('#stop', el).addEventListener('click', async () => {
      const done = stopClock(clock, Date.now());
      if (!done) {
        await saveChange({ ...clock, deleted: true, updatedAt: Date.now() });
        await setClock(null);
        toast('Weniger als eine Minute – Stempelung verworfen.', 'warn');
      } else {
        await setClock(null);
        await saveChange(done);
        toast(`Gegangen um ${done.end}. Gespeichert.`);
      }
      void renderHome(el);
    });
  } else {
    $('#start', el).addEventListener('click', async () => {
      const activity = ($('#activity', el) as HTMLSelectElement).value;
      localStorage.setItem('ze:lastActivity', activity);
      const e = startClock(Date.now(), activity);
      const clash = findOverlaps({ ...e }, entries.filter((x) => !x.deleted));
      if (clash.length) {
        toast(`Überschneidung mit ${clash[0].start}–${clash[0].end ?? '…'}. Bitte Einträge prüfen.`, 'error');
        return;
      }
      await setClock(e);
      await saveChange(e);
      toast(`Gekommen um ${e.start}.`);
      void renderHome(el);
    });
  }
}

/** Müll-Banner neu zeichnen (auch vom Polling aufgerufen). */
export async function refreshWasteBox(): Promise<void> {
  const w = await loadWaste();
  const box = document.getElementById('waste');
  if (!box) return;
  box.innerHTML = wasteBanner(w);
  bindPutouts(box, () => void refreshWasteBox());
}

export function stopHomeTimer(): void {
  window.clearInterval(tick);
  window.clearInterval(poll);
  window.clearInterval(countdown);
}
